import { Component, Element, h, Host, Listen, Prop, Watch } from '@stencil/core'

import Helpers from '../../../utils/helpers'
import { CssClassMap } from '../../../utils/interfaces'

type HorizontalAlign = 'left' | 'center' | 'right'

enum CssVariables {
  HORIZONTAL_ALIGN = '--horizontal-align',
}

/**
 * Para uma descrição detalhada, consulte a [documentação do GovBR-DS](https://www.gov.br/ds/components/table?tab=designer).
 *
 * @slot default - Descrição do slot
 */
@Component({
  tag: 'br-table-cell',
  styleUrl: 'table-cell.scss',
  shadow: true,
})
export class TableCell {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrTableCellElement

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Define se a célula do cabeçalho possui borda vertical.
   */
  @Prop({ reflect: true }) hasVerticalBorder: boolean = false

  /**
   * Define o alinhamento horizontal do conteúdo da célula.
   */
  @Prop({ reflect: true }) horizontalAlign: HorizontalAlign = 'left'

  /**
   * Watcher que observa as mudanças na propriedade `horizontalAlign`.
   */
  @Watch('horizontalAlign')
  handleHorizontalAlignChange(newValue: HorizontalAlign) {
    this.el.style.setProperty(CssVariables.HORIZONTAL_ALIGN, newValue)
  }

  /**
   * Ouve o evento disparado ao alterar o alinhamento horizontal do conteúdo da célula.
   * @param event Objeto Event
   */
  @Listen('brHorizontalAlignDidChange', { target: 'document' })
  handleHorizontalAlignDidChange(event: CustomEvent) {
    if (!this.el.hasAttribute('horizontal-align')) {
      this.el.style.setProperty(CssVariables.HORIZONTAL_ALIGN, event.detail)
    }
  }

  /**
   * Ciclo de vida do componente. Chamado após o componente ser renderizado no DOM.
   */
  connectedCallback() {
    this.handleHorizontalAlignChange(this.horizontalAlign)
  }

  /**
   * Define um conjunto de classes CSS que serão aplicadas ao componente.
   * @returns Objeto com as classes CSS.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-table-cell': true,
      'vertical-border': this.hasVerticalBorder,
    }
  }

  /**
   * Define o JSX do componente.
   * @returns JSX do componente.
   */
  render() {
    return (
      <Host>
        <div id={this.customId} class={this.getCssClassMap()}>
          <slot></slot>
        </div>
      </Host>
    )
  }
}
